import { useCallback, useLayoutEffect, useRef, useState } from "react";
import apiClient from "../business/network/ApiClient";
import { parseScheduledResponse } from "../business/helper/ScheduledFeed";

// A scheduled prompt's feed is opened on its latest page (the newest answer at the bottom,
// where the chat lands) and grows upwards as the reader scrolls back in time, the way a
// messaging history does: reaching the top asks the server for the page before the oldest
// answer on screen, which is prepended while the viewport stays on what the reader was
// looking at.
//
// The hook owns the paging state and the scroll mechanics; the page owns the entries. It
// hands the page `onScroll` for the scroll container and `prepend(entries)` is the page's
// way of adding a loaded page in front of the conversation. `prependedRef` is raised for the
// commit that carries a prepend, so the page's own "scroll to the end on every change" and
// "save the conversation" effects can tell a loaded page from a new turn.

export const PAGE_SIZE = 26;
// This close to the top of the scroll container asks for the page before.
export const TOP_THRESHOLD_PX = 80;
// The viewport must first have been this far down: a feed opens at the top and is scrolled
// to its end, and that opening scroll must not read as "the reader wants older answers".
export const ARM_DISTANCE_PX = 200;

const defaultFetchPage = (questionId, beforeId) =>
  apiClient.getScheduledResponses(questionId, beforeId, PAGE_SIZE);

export default function useScheduledFeedPaging({ scrollRef, prepend, fetchPage = defaultFetchPage }) {
  // { questionId, hasMore, oldestId } of the open feed; null when the chat shows no feed.
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(false);
  // Older pages loaded since the feed was opened — the page can say "that was the first
  // answer" once there is a history behind the reader.
  const [pagesLoaded, setPagesLoaded] = useState(0);
  const [version, setVersion] = useState(0);

  const feedRef = useRef(null);
  const loadingRef = useRef(false);
  const armedRef = useRef(false);
  const restoreRef = useRef(null);
  const prependedRef = useRef(false);

  const open = useCallback((meta) => {
    feedRef.current = meta ?? null;
    armedRef.current = false;
    restoreRef.current = null;
    setFeed(meta ?? null);
    setPagesLoaded(0);
  }, []);

  const close = useCallback(() => open(null), [open]);

  const loadOlder = useCallback(async () => {
    const current = feedRef.current;
    if (!current || !current.hasMore || current.oldestId == null || loadingRef.current) return false;

    loadingRef.current = true;
    setLoading(true);
    try {
      const page = await fetchPage(current.questionId, current.oldestId);
      // The reader opened another feed meanwhile: this page belongs to the one they left.
      if (feedRef.current !== current) return false;

      const entries = (page?.responses ?? []).map(parseScheduledResponse);
      const element = scrollRef.current;
      restoreRef.current = element ? { height: element.scrollHeight, top: element.scrollTop } : null;

      const next = {
        ...current,
        hasMore: entries.length > 0 && page?.hasMore === true,
        oldestId: entries.length > 0 ? entries[0].id ?? current.oldestId : current.oldestId,
      };
      feedRef.current = next;
      if (entries.length > 0) {
        prependedRef.current = true;
        prepend(entries);
      }
      setFeed(next);
      setPagesLoaded((count) => count + 1);
      setVersion((v) => v + 1);
      return entries.length > 0;
    } catch (error) {
      console.error("Failed to load the earlier scheduled answers:", error);
      return false;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [fetchPage, prepend, scrollRef]);

  // After a prepend commits, put the viewport back on the answer the reader was looking at:
  // whatever height arrived above it is added to the scroll offset.
  useLayoutEffect(() => {
    const snapshot = restoreRef.current;
    const element = scrollRef.current;
    if (!snapshot || !element) return;
    restoreRef.current = null;
    element.scrollTop = element.scrollHeight - snapshot.height + snapshot.top;
  }, [version, scrollRef]);

  const atTop = (element) => element.scrollTop <= TOP_THRESHOLD_PX;

  const onScroll = useCallback(() => {
    const element = scrollRef.current;
    const current = feedRef.current;
    if (!element || !current?.hasMore) return;

    if (element.scrollTop > ARM_DISTANCE_PX) {
      armedRef.current = true;
      return;
    }
    if (armedRef.current && atTop(element) && !loadingRef.current) {
      armedRef.current = false;
      loadOlder();
    }
  }, [loadOlder, scrollRef]);

  // A wheel turned upwards at the top is the reader asking for more, whatever the scroll
  // position did before (a feed interrupted mid-way through its opening scroll, say).
  const onWheel = useCallback(
    (event) => {
      const element = scrollRef.current;
      if (!element || event.deltaY >= 0 || !feedRef.current?.hasMore) return;
      if (atTop(element) && !loadingRef.current) {
        armedRef.current = false;
        loadOlder();
      }
    },
    [loadOlder, scrollRef]
  );

  return { feed, loading, pagesLoaded, onScroll, onWheel, open, close, loadOlder, prependedRef };
}
